// ABOUTME: The 5D bird's-eye sky and sea: the summary sky, the horizon, eight drifting swell bands and the warm glow.
// ABOUTME: Positions are the prototype's scene pixels with the horizon at 152; the scene shifts the whole sea into place.

import { Box } from '@mui/material';
import {
  CROSSING_SKY_SUMMARY_BOTTOM,
  CROSSING_SKY_SUMMARY_MID,
  CROSSING_SKY_SUMMARY_TOP,
  CROSSING_SUMMARY_GLOW,
  CROSSING_SUMMARY_HORIZON_LINE,
  CROSSING_SUMMARY_SEA_BASE,
} from '../../../../theme/crossingTokens';
import { SUMMARY_SWELL_BANDS } from '../../../../theme/waveAssets';

export const SUMMARY_HORIZON_TOP = 152;
const SIDE_BLEED = 2400; // a multiple of 480, the drift distance, so every band tiles on across the bleed
const BOTTOM_BLEED = -2000;
const LAST_FILL_TOP = 596;

// The solid fill under a band starts where the next band does, so each swell's crest rides on the colour above it.
const fillTopOf = (index: number) => SUMMARY_SWELL_BANDS[index + 1]?.top ?? LAST_FILL_TOP;

interface SummarySeaProps {
  reducedMotion: boolean;
}

export function SummarySea({ reducedMotion }: SummarySeaProps) {
  return (
    <Box sx={{ position: 'absolute', inset: 0 }}>
      <Box
        data-testid="summary-sky"
        sx={{
          position: 'absolute', left: -SIDE_BLEED, right: -SIDE_BLEED, top: -60, height: 212,
          backgroundImage: `linear-gradient(${CROSSING_SKY_SUMMARY_TOP} 0%, ${CROSSING_SKY_SUMMARY_MID} 60%, ${CROSSING_SKY_SUMMARY_BOTTOM} 100%)`,
        }}
      />
      <Box
        data-testid="summary-horizon"
        sx={{ position: 'absolute', left: -SIDE_BLEED, right: -SIDE_BLEED, top: SUMMARY_HORIZON_TOP, height: '1px', background: CROSSING_SUMMARY_HORIZON_LINE, zIndex: 2 }}
      />
      <Box
        data-testid="summary-sea-base"
        sx={{ position: 'absolute', left: -SIDE_BLEED, right: -SIDE_BLEED, top: SUMMARY_HORIZON_TOP, bottom: BOTTOM_BLEED, background: CROSSING_SUMMARY_SEA_BASE }}
      />
      {SUMMARY_SWELL_BANDS.map((band, index) => (
        <Box key={band.top} sx={{ display: 'contents' }}>
          <Box
            data-testid={`summary-sea-band-${index + 1}`}
            data-seconds={band.seconds}
            sx={{
              position: 'absolute', left: -SIDE_BLEED, right: -SIDE_BLEED, top: band.top, height: band.height,
              backgroundImage: band.url, backgroundSize: `${band.width}px ${band.height}px`, backgroundRepeat: 'repeat-x', backgroundPosition: '0 0',
              '@keyframes pvSwell': { from: { backgroundPositionX: '0' }, to: { backgroundPositionX: '-480px' } },
              animation: reducedMotion ? 'none' : `pvSwell ${band.seconds}s linear infinite`,
            }}
          />
          <Box
            data-testid={`summary-sea-fill-${index + 1}`}
            sx={{ position: 'absolute', left: -SIDE_BLEED, right: -SIDE_BLEED, top: fillTopOf(index), bottom: BOTTOM_BLEED, backgroundColor: band.fill }}
          />
        </Box>
      ))}
      <Box
        data-testid="summary-glow"
        sx={{ position: 'absolute', left: 668, top: 60, width: 560, height: 420, background: CROSSING_SUMMARY_GLOW, zIndex: 3 }}
      />
    </Box>
  );
}
